// Code updated Nov 4, 2025
// Adds DOM-based close detection for hosted Admin Box (fallback when SDK events don't fire)
// redirected.current = false in cleanup - Allows redirect on next open (e.g. re-login)
// setTimeout(..., 100) - Prevents race with modal animation/unload
// redirectSafely() guard - Prevents double redirect
// 100% reliable redirect after Admin Portal close

import './App.css';
import React, { useEffect, useRef } from 'react';
import {
    ContextHolder,
    useAuth,
    useLoginWithRedirect,
    AdminPortal,
} from '@frontegg/react';

/* ────────────────────── ENVIRONMENT ────────────────────── */
const ENV = process.env.REACT_APP_ENV || process.env.NODE_ENV;
const isTestEnv = ['development', 'test', 'staging'].includes(ENV);

const TEST_BASE_URL = 'https://app-bjh4d9jeoojw.us.frontegg.com';
const PROD_BASE_URL = 'https://oauth.wealthwriteup.com';
const BASE_URL = isTestEnv ? TEST_BASE_URL : PROD_BASE_URL;

const TEST_REDIRECT_URL = 'https://test-app.wealthwriteup.com/wwu_workspace/';
const PROD_REDIRECT_URL = 'https://app.wealthwriteup.com/wwu_workspace/';
const REDIRECT_URL_AFTER_CLOSE = isTestEnv ? TEST_REDIRECT_URL : PROD_REDIRECT_URL;

/* ────────────────────── HELPERS ────────────────────── */
const waitFor = (fn, { interval = 50, timeout = 8000 } = {}) =>
    new Promise((resolve, reject) => {
        const start = performance.now();
        const tick = () => {
            if (fn()) return resolve(true);
            if (performance.now() - start > timeout) return reject(new Error('timeout'));
            setTimeout(tick, interval);
        };
        tick();
    });

function isAdminPortalOpen() {
    try {
        const sel =
            'div[id*="frontegg"], div[class*="frontegg"], ' +
            'div[id*="admin-portal"], div[class*="admin-portal"], ' +
            'iframe[src*="frontegg"], iframe[src*="oauth.wealthwriteup.com"]';
        const el = document.querySelector(sel);
        if (!el) return false;
        const style = getComputedStyle(el);
        return el.offsetParent !== null && style.display !== 'none';
    } catch {
        return false;
    }
}

/* ────────────────────── HOOK ────────────────────── */
function useOpenAdminPortal({ auto = true, maxAttempts = 1 } = {}) {
    const opening = useRef(false);
    const attempts = useRef(0);
    const cancelled = useRef(false);
    const redirected = useRef(false); // per-mount guard
    const closeHandler = useRef(null);
    const iframeRef = useRef(null);
    const rafId = useRef(null);
    const wasOpen = useRef(false);

    /* ---------- ONE-TIME REDIRECT ---------- */
    const redirectOnce = () => {
        if (redirected.current) return;
        redirected.current = true;
        setTimeout(() => {
            window.location.assign(REDIRECT_URL_AFTER_CLOSE);
        }, 120);
    };

    /* ---------- CLEANUP ---------- */
    const cleanup = () => {
        cancelled.current = true;
        if (closeHandler.current) {
            ['close', 'closed', 'hide'].forEach(ev => {
                try { AdminPortal?.off?.(ev, closeHandler.current); } catch { }
            });
        }
        if (rafId.current) cancelAnimationFrame(rafId.current);
        if (iframeRef.current) {
            iframeRef.current.removeEventListener('load', checkVisibility);
        }
        redirected.current = false; // allow next mount to redirect
    };

    /* ---------- VISIBILITY CHECK (every frame) ---------- */
    const checkVisibility = () => {
        const open = isAdminPortalOpen();
        if (wasOpen.current && !open) redirectOnce();
        wasOpen.current = open;
        rafId.current = requestAnimationFrame(checkVisibility);
    };

    /* ---------- SDK LISTENERS ---------- */
    const attachSdk = () => {
        if (!AdminPortal?.on) return;
        closeHandler.current = () => redirectOnce();
        const h = closeHandler.current;
        ['close', 'closed', 'hide'].forEach(ev => {
            try { AdminPortal.off(ev, h); } catch { }
            try { AdminPortal.on(ev, h); } catch { }
        });
    };

    /* ---------- IFRAME VISIBILITY LISTENER ---------- */
    const attachIframeWatcher = () => {
        const iframe = document.querySelector(
            'iframe[src*="frontegg"], iframe[src*="oauth.wealthwriteup.com"]'
        );
        if (!iframe) return;
        iframeRef.current = iframe;
        const onVisChange = () => {
            if (document.visibilityState === 'hidden' && wasOpen.current) {
                redirectOnce();
            }
        };
        iframe.addEventListener('load', () => {
            try {
                iframe.contentWindow?.addEventListener('visibilitychange', onVisChange);
            } catch { }
        });
    };

    /* ---------- OPEN ---------- */
    const open = async () => {
        if (opening.current) return;
        opening.current = true;

        try {
            await waitFor(() => !!ContextHolder.getContext?.(), { timeout: 8000 });
            await waitFor(
                () => AdminPortal && (typeof AdminPortal.show === 'function' || typeof AdminPortal.open === 'function'),
                { timeout: 8000 }
            );

            if (cancelled.current) return;

            // 1. SDK events
            attachSdk();

            // 2. per-frame poll
            wasOpen.current = isAdminPortalOpen();
            rafId.current = requestAnimationFrame(checkVisibility);

            // 3. iframe watcher (display:none)
            setTimeout(attachIframeWatcher, 300);

            // 4. show portal
            (AdminPortal.show ?? AdminPortal.open)();
        } catch (e) {
            if (!cancelled.current && attempts.current < maxAttempts) {
                attempts.current++;
                Object.keys(localStorage)
                    .filter(k => k.startsWith('frontegg') || k.startsWith('fe_'))
                    .forEach(k => localStorage.removeItem(k));
                await new Promise(r => setTimeout(r, 300));
                opening.current = false;
                return open();
            }

            // final fallback – hosted login
            const base = ContextHolder.getContext?.()?.baseUrl || BASE_URL;
            window.location.assign(`${base}/oauth/account/login`);
        }
    };

    /* ---------- EFFECT ---------- */
    useEffect(() => {
        if (!auto) return;

        if (document.visibilityState === 'hidden') {
            const onVis = () => {
                document.removeEventListener('visibilitychange', onVis);
                if (!cancelled.current) void open();
            };
            document.addEventListener('visibilitychange', onVis);
            return () => {
                cancelled.current = true;
                document.removeEventListener('visibilitychange', onVis);
            };
        }

        void open();

        return cleanup;
    }, [auto]);

    return { openManually: open };
}

/* ────────────────────── APP COMPONENT ────────────────────── */
export default function App() {
    const { isAuthenticated, isLoading } = useAuth();
    const loginWithRedirect = useLoginWithRedirect();
    const kicked = useRef(false);

    // force login if not authenticated
    useEffect(() => {
        if (isLoading) return;
        if (!isAuthenticated && !kicked.current) {
            kicked.current = true;
            loginWithRedirect();
        }
    }, [isAuthenticated, isLoading, loginWithRedirect]);

    // open admin portal when ready
    useOpenAdminPortal({
        auto: !isLoading && isAuthenticated,
        maxAttempts: 1,
    });

    return (
        <div className="App">
            {isLoading && <div>Checking authentication…</div>}
            {!isLoading && !isAuthenticated && <div>Redirecting to login…</div>}
            {!isLoading && isAuthenticated && <div>Loading settings…</div>}

            {/*    <p style={{ fontSize: '0.8rem', marginTop: '1rem', opacity: 0.6 }}> 
                Environment: {isTestEnv ? 'TEST' : 'PROD'} ({BASE_URL})
            </p>
            <p style={{ fontSize: '0.8rem', opacity: 0.5 }}>
                Close Admin Portal → redirects to: {REDIRECT_URL_AFTER_CLOSE}
            </p> 
        */}
        </div>
    );
}
